import React, { useEffect, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Radio, Map as MapIcon, Database, Cloud, Wifi, ShieldCheck } from "lucide-react";

const STEPS = [
  { label: "Initializing Maps Engine", icon: MapIcon },
  { label: "Loading Incident Database", icon: Database },
  { label: "Connecting Weather Services (NWS)", icon: Cloud },
  { label: "Joining Paging Network", icon: Radio },
  { label: "Verifying Comms Channel", icon: Wifi },
  { label: "System Ready", icon: ShieldCheck },
];

const STATUS_TICKER = [
  "[NWS] No active severe weather alerts in service area",
  "[CAD] 17 units available • 0 active pursuits",
  "[OPS] Shift change in 02:14:36",
  "[INTEL] Mutual-aid channel synced with neighboring agencies",
  "[NET] Encrypted P25 link nominal • latency 14ms",
];

export default function BootScreen({ onComplete }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [tickerIdx, setTickerIdx] = useState(0);

  useEffect(() => {
    if (stepIdx >= STEPS.length) {
      const t = setTimeout(() => onComplete?.(), 600);
      return () => clearTimeout(t);
    }
    const dur = 550;
    const start = Date.now();
    const iv = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / dur) * 100);
      setProgress(((stepIdx + pct / 100) / STEPS.length) * 100);
      if (elapsed >= dur) {
        clearInterval(iv);
        setStepIdx((s) => s + 1);
      }
    }, 30);
    return () => clearInterval(iv);
  }, [stepIdx, onComplete]);

  useEffect(() => {
    const t = setInterval(() => setTickerIdx((i) => (i + 1) % STATUS_TICKER.length), 1800);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="boot-screen"
      className="fixed inset-0 z-50 bg-background text-foreground flex flex-col items-center justify-center grid-bg overflow-hidden"
    >
      <div className="scanlines absolute inset-0 pointer-events-none" />

      {/* Header */}
      <div className="absolute top-8 left-8 right-8 flex justify-between items-center font-mono text-xs text-muted-foreground">
        <div>SYS://CAD.DISPATCH.CORE</div>
        <div>v1.0.0 • {new Date().toUTCString().slice(17, 25)} UTC</div>
      </div>

      <div className="w-full max-w-2xl px-8 z-10">
        {/* Logo / Title */}
        <div className="flex flex-col items-center mb-12">
          <div className="relative">
            <img src="/branding/psa-logo.png" alt="Public Safety Alerts" className="w-28 h-28 object-contain mb-4 drop-shadow-[0_0_24px_rgba(59,130,246,0.4)]" />
            <div className="absolute inset-0 rounded-full animate-ping border-2 border-primary/20 pointer-events-none" />
          </div>
          <h1 className="text-3xl font-bold tracking-[0.3em] mb-1">PUBLIC SAFETY ALERTS</h1>
          <p className="text-xs font-mono text-muted-foreground tracking-widest">
            TRUSTED ALERTS FOR PUBLIC SAFETY
          </p>
        </div>

        {/* Steps list */}
        <div className="space-y-2 mb-6 font-mono text-sm">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < stepIdx;
            const active = i === stepIdx;
            return (
              <div
                key={s.label}
                data-testid={`boot-step-${i}`}
                className={`flex items-center gap-3 transition-opacity ${
                  done ? "opacity-100" : active ? "opacity-100" : "opacity-30"
                }`}
              >
                <Icon className={`w-4 h-4 ${done ? "text-green-500" : active ? "text-primary" : ""}`} />
                <span className="flex-1">{s.label}</span>
                <span className={`text-xs ${done ? "text-green-500" : active ? "text-primary typing" : "text-muted-foreground"}`}>
                  {done ? "[ OK ]" : active ? "[....]" : "[----]"}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <Progress data-testid="boot-progress" value={progress} className="h-2" />
        </div>
        <div className="flex justify-between text-xs font-mono text-muted-foreground">
          <span>{Math.floor(progress)}% LOADED</span>
          <span>ESTABLISHING SECURE LINK</span>
        </div>

        {/* Status ticker */}
        <div className="mt-12 border border-border bg-card/50 backdrop-blur rounded-sm p-4 font-mono text-xs text-muted-foreground">
          <div className="text-primary mb-2">// LIVE OPS BULLETIN</div>
          <div key={tickerIdx} data-testid="boot-ticker" className="terminal-text">
            {STATUS_TICKER[tickerIdx]}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-8 right-8 flex justify-between font-mono text-[10px] text-muted-foreground tracking-widest">
        <span>SECURE • CJIS-COMPLIANT • AES-256</span>
        <span>© 2026 PUBLIC SAFETY ALERTS</span>
      </div>
    </div>
  );
}
